import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Calendar, Users, Package, DollarSign, Plus, Play, Archive, 
    Loader2, QrCode, UserCheck, UserMinus, ArrowLeft, X, Camera, Search,
    ChevronDown, ChevronUp, Download, Ticket, Heart, ShoppingBag, Pencil, FileText, Trash2, Eye, EyeOff, Image as ImageIcon, Upload
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Lazy load QR scanner - only loaded when admin uses scan feature
const QrReader = lazy(() => import('react-qr-reader').then(module => ({ default: module.QrReader })));

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const ARTICLE_CATEGORIES = ["Style & Closet", "Event Updates", "Community", "Charity"];

const AdminPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [activeTab, setActiveTab] = useState('scan');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [events, setEvents] = useState([]);
    const [guestList, setGuestList] = useState([]);
    const [users, setUsers] = useState([]);
    const [currentEvent, setCurrentEvent] = useState(null);
    
    // Items state (for auction management)
    const [adminItems, setAdminItems] = useState([]);
    
    // Articles state (for news management)
    const [articles, setArticles] = useState([]);
    const [showArticleForm, setShowArticleForm] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [articleForm, setArticleForm] = useState({
        title: '',
        subtitle: '',
        category: 'Style & Closet',
        content: '',
        header_image: '',
        inline_images: [],
        is_published: false
    });
    const [itemsLoading, setItemsLoading] = useState(false);
    const [selectedItemForStatus, setSelectedItemForStatus] = useState(null);
    const [showItemStatusModal, setShowItemStatusModal] = useState(false);
    const [itemStatusForm, setItemStatusForm] = useState({ status: 'sold', final_price: '' });
    
    // Finance state
    const [financeData, setFinanceData] = useState(null);
    const [financeLoading, setFinanceLoading] = useState(false);
    const [selectedEventFinance, setSelectedEventFinance] = useState(null);
    const [eventFinanceData, setEventFinanceData] = useState(null);
    const [eventFinanceLoading, setEventFinanceLoading] = useState(false);
    const [showAddCost, setShowAddCost] = useState(false);
    const [showEditCost, setShowEditCost] = useState(false);
    const [editingCost, setEditingCost] = useState(null);
    const [selectedEventForCost, setSelectedEventForCost] = useState(null);
    const [costForm, setCostForm] = useState({ description: '', amount: '' });
    const [expandedSections, setExpandedSections] = useState({}); // Track expanded accordion sections
    
    // Tickets & Donations management state
    const [allTickets, setAllTickets] = useState([]);
    const [allDonations, setAllDonations] = useState([]);
    const [showTicketsManager, setShowTicketsManager] = useState(false);
    const [showDonationsManager, setShowDonationsManager] = useState(false);
    const [showAddDonation, setShowAddDonation] = useState(false);
    const [manualDonation, setManualDonation] = useState({ donor_name: '', amount: '', note: '' });
    
    // QR Scanning state
    const [cameraActive, setCameraActive] = useState(true); // Camera-first approach
    const [cameraError, setCameraError] = useState(null);
    const [manualUserId, setManualUserId] = useState('');
    const [scannedUser, setScannedUser] = useState(null);
    const [swapCreditsInput, setSwapCreditsInput] = useState('');
    const [processing, setProcessing] = useState(false);
    const [swapItemsInput, setSwapItemsInput] = useState(''); // For check-in: number of physical items brought
    
    // Event creation/edit state
    const [showCreateEvent, setShowCreateEvent] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null); // null = create mode, event = edit mode
    const [eventForm, setEventForm] = useState({
        title: '',
        season_tag: 'SS',
        start_datetime: '',
        end_datetime: '',
        location_name: '',
        address: '',
        program_text: '',
        ticket_price: 0,
        capacity: 50,
        is_open_access: false,
    });
    const [submitting, setSubmitting] = useState(false);
    
    // Guest List state
    const [showAddGuest, setShowAddGuest] = useState(false);
    const [guestForm, setGuestForm] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        role: 'guest'
    });
    const [guestFilter, setGuestFilter] = useState('all'); // 'all', 'hsiu-li', 'jessica', 'not-invited'
    const [selectedGuests, setSelectedGuests] = useState([]);
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [invitationTemplate, setInvitationTemplate] = useState(null);
    const [showEditInvitation, setShowEditInvitation] = useState(false);
    
    // User detail view state
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserDetail, setShowUserDetail] = useState(false);
    const [userDetailLoading, setUserDetailLoading] = useState(false);

    // Page content state (for editable About & House Rules)
    const [aboutContent, setAboutContent] = useState(null);
    const [houseRulesContent, setHouseRulesContent] = useState(null);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [editingPage, setEditingPage] = useState(null); // 'about' or 'house-rules'
    const [pageFormData, setPageFormData] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    // Fetch finance data when Finance tab is selected
    useEffect(() => {
        if (activeTab === 'finance' && !financeData) {
            fetchFinanceData();
        }
    }, [activeTab]);

    // Fetch page content when Pages tab is selected
    useEffect(() => {
        if (activeTab === 'pages' && !aboutContent) {
            fetchPageContent();
        }
    }, [activeTab]);

    const fetchPageContent = async () => {
        setPagesLoading(true);
        try {
            const [aboutRes, rulesRes] = await Promise.all([
                api().get('/content/about'),
                api().get('/content/house-rules')
            ]);
            setAboutContent(aboutRes.data);
            setHouseRulesContent(rulesRes.data);
        } catch (error) {
            console.error('Failed to fetch page content:', error);
            toast.error('Failed to load page content');
        } finally {
            setPagesLoading(false);
        }
    };

    const handleSaveAbout = async () => {
        setSubmitting(true);
        try {
            await api().put('/content/about', pageFormData);
            toast.success('About page updated!');
            setEditingPage(null);
            fetchPageContent();
        } catch (error) {
            toast.error('Failed to update About page');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveHouseRules = async () => {
        setSubmitting(true);
        try {
            await api().put('/content/house-rules', pageFormData);
            toast.success('House Rules updated!');
            setEditingPage(null);
            fetchPageContent();
        } catch (error) {
            toast.error('Failed to update House Rules');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchData = async () => {
        try {
            const [statsRes, eventsRes, guestListRes, usersRes] = await Promise.all([
                api().get('/admin/stats'),
                api().get('/events/'),
                api().get('/admin/guest-list'),
                api().get('/admin/users'),
            ]);
            setStats(statsRes.data);
            setEvents(eventsRes.data);
            setGuestList(guestListRes.data);
            setUsers(usersRes.data);
            
            // Find current/upcoming event
            const current = eventsRes.data.find(e => e.status === 'live' || e.status === 'upcoming');
            setCurrentEvent(current);
        } catch (error) {
            console.error('Failed to fetch admin data:', error);
            toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    // Fetch items when Items tab is selected
    useEffect(() => {
        if (activeTab === 'items' && adminItems.length === 0) {
            fetchAdminItems();
        }
    }, [activeTab]);

    const fetchAdminItems = async (eventId = null) => {
        setItemsLoading(true);
        try {
            const url = eventId ? `/admin/items?event_id=${eventId}` : '/admin/items';
            const response = await api().get(url);
            setAdminItems(response.data);
        } catch (error) {
            console.error('Failed to fetch items:', error);
            toast.error('Failed to load items');
        } finally {
            setItemsLoading(false);
        }
    };

    const handleUpdateItemStatus = async (e) => {
        e.preventDefault();
        if (!selectedItemForStatus) return;
        
        if (itemStatusForm.status === 'sold' && (!itemStatusForm.final_price || parseFloat(itemStatusForm.final_price) <= 0)) {
            toast.error('Please enter a valid final price');
            return;
        }
        
        setSubmitting(true);
        try {
            await api().put(`/admin/items/${selectedItemForStatus.id}/status`, {
                status: itemStatusForm.status,
                final_price: itemStatusForm.status === 'sold' ? parseFloat(itemStatusForm.final_price) : null
            });
            toast.success(`Item marked as ${itemStatusForm.status}`);
            setShowItemStatusModal(false);
            setSelectedItemForStatus(null);
            setItemStatusForm({ status: 'sold', final_price: '' });
            fetchAdminItems();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update item status');
        } finally {
            setSubmitting(false);
        }
    };

    // Fetch articles when News tab is selected
    useEffect(() => {
        if (activeTab === 'news' && articles.length === 0) {
            fetchArticles();
        }
    }, [activeTab]);
    
    // Auto-request camera permission when Scan tab is opened on mobile
    useEffect(() => {
        if (activeTab === 'scan' && !cameraActive && !cameraError) {
            // Small delay to let the tab render first
            const timer = setTimeout(() => {
                requestCameraPermission();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [activeTab]);

    const fetchArticles = async () => {
        try {
            const response = await api().get('/news/');
            setArticles(response.data);
        } catch (error) {
            console.error('Failed to fetch articles:', error);
            toast.error('Failed to load articles');
        }
    };

    const handleArticleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (event) => {
            if (type === 'header') {
                setArticleForm(prev => ({ ...prev, header_image: event.target.result }));
            } else if (type === 'inline') {
                if (articleForm.inline_images.length >= 3) {
                    toast.error('Maximum 3 inline images allowed');
                    return;
                }
                setArticleForm(prev => ({
                    ...prev,
                    inline_images: [...prev.inline_images, event.target.result]
                }));
            }
        };
        reader.readAsDataURL(file);
    };

    const removeInlineImage = (index) => {
        setArticleForm(prev => ({
            ...prev,
            inline_images: prev.inline_images.filter((_, i) => i !== index)
        }));
    };

    const resetArticleForm = () => {
        setArticleForm({
            title: '',
            subtitle: '',
            category: 'Style & Closet',
            content: '',
            header_image: '',
            inline_images: [],
            is_published: false
        });
        setEditingArticle(null);
        setShowArticleForm(false);
    };

    const handleCreateArticle = async (e) => {
        e.preventDefault();
        
        if (!articleForm.title.trim()) {
            toast.error('Title is required');
            return;
        }
        
        setSubmitting(true);
        try {
            if (editingArticle) {
                await api().put(`/news/${editingArticle.id}`, articleForm);
                toast.success('Article updated');
            } else {
                await api().post('/news/', articleForm);
                toast.success('Article created');
            }
            resetArticleForm();
            fetchArticles();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save article');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditArticle = (article) => {
        setEditingArticle(article);
        setArticleForm({
            title: article.title || '',
            subtitle: article.subtitle || '',
            category: article.category || 'Style & Closet',
            content: article.content || '',
            header_image: article.header_image || '',
            inline_images: article.inline_images || [],
            is_published: article.is_published || false
        });
        setShowArticleForm(true);
    };

    const handleDeleteArticle = async (articleId) => {
        if (!window.confirm('Are you sure you want to delete this article?')) return;
        
        try {
            await api().delete(`/news/${articleId}`);
            toast.success('Article deleted');
            fetchArticles();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete article');
        }
    };

    const handleTogglePublish = async (article) => {
        try {
            await api().put(`/news/${article.id}`, { is_published: !article.is_published });
            toast.success(article.is_published ? 'Article unpublished' : 'Article published');
            fetchArticles();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update article');
        }
    };

    const fetchFinanceData = async () => {
        setFinanceLoading(true);
        try {
            const response = await api().get('/admin/finance');
            setFinanceData(response.data);
            // Auto-select first event if available
            if (response.data.events?.length > 0 && !selectedEventFinance) {
                setSelectedEventFinance(response.data.events[0].event_id);
            }
        } catch (error) {
            console.error('Failed to fetch finance data:', error);
            toast.error('Failed to load finance data');
        } finally {
            setFinanceLoading(false);
        }
    };

    const fetchAllTickets = async () => {
        try {
            const response = await api().get('/admin/finance/tickets');
            setAllTickets(response.data || []);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
        }
    };

    const fetchAllDonations = async () => {
        try {
            const response = await api().get('/admin/finance/donations');
            setAllDonations(response.data || []);
        } catch (error) {
            console.error('Failed to fetch donations:', error);
        }
    };

    const handleDeleteTicket = async (ticketId) => {
        if (!window.confirm('Weet je zeker dat je dit ticket wilt verwijderen?')) return;
        try {
            await api().delete(`/admin/finance/tickets/${ticketId}`);
            toast.success('Ticket verwijderd');
            fetchAllTickets();
            fetchFinanceData();
        } catch (error) {
            toast.error('Kon ticket niet verwijderen');
        }
    };

    const handleDeleteDonation = async (donationId) => {
        if (!window.confirm('Weet je zeker dat je deze donatie wilt verwijderen?')) return;
        try {
            await api().delete(`/admin/finance/donations/${donationId}`);
            toast.success('Donatie verwijderd');
            fetchAllDonations();
            fetchFinanceData();
        } catch (error) {
            toast.error('Kon donatie niet verwijderen');
        }
    };

    const fetchEventFinanceData = async (eventId) => {
        if (!eventId) return;
        setEventFinanceLoading(true);
        try {
            const response = await api().get(`/admin/finance/${eventId}`);
            setEventFinanceData(response.data);
            setExpandedSections({}); // Reset expanded sections when switching events
        } catch (error) {
            console.error('Failed to fetch event finance data:', error);
            toast.error('Failed to load event details');
        } finally {
            setEventFinanceLoading(false);
        }
    };

    // Fetch event details when selected event changes
    useEffect(() => {
        if (selectedEventFinance) {
            fetchEventFinanceData(selectedEventFinance);
        }
    }, [selectedEventFinance]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const exportFinanceCSV = () => {
        if (!eventFinanceData) return;
        
        const rows = [];
        const event = eventFinanceData;
        
        // Header
        rows.push(['Finance Report - ' + event.event_title]);
        rows.push(['Generated: ' + new Date().toISOString()]);
        rows.push([]);
        
        // Summary
        rows.push(['SUMMARY']);
        rows.push(['Ticket Revenue', '€' + event.ticket_revenue.toFixed(2)]);
        rows.push(['Donation Revenue', '€' + event.donation_revenue.toFixed(2)]);
        rows.push(['Auction Revenue', '€' + event.auction_revenue.toFixed(2)]);
        rows.push(['Total Revenue', '€' + event.total_revenue.toFixed(2)]);
        rows.push(['Total Costs', '€' + event.total_costs.toFixed(2)]);
        rows.push(['Net Donation', '€' + event.net_donation.toFixed(2)]);
        rows.push([]);
        
        // Ticket Sales
        rows.push(['TICKET SALES']);
        rows.push(['Buyer', 'Tickets', 'Amount', 'Payment Confirmed', 'Date']);
        event.ticket_sales.forEach(sale => {
            rows.push([
                sale.buyer_name,
                sale.ticket_count,
                '€' + sale.total_amount.toFixed(2),
                sale.payment_confirmed ? 'Yes' : 'No',
                sale.created_at ? new Date(sale.created_at).toLocaleDateString() : ''
            ]);
        });
        rows.push([]);
        
        // Donations
        rows.push(['DONATIONS']);
        rows.push(['Donor', 'Amount', 'Stripe Reference', 'Date']);
        event.donations.forEach(donation => {
            rows.push([
                donation.donor_name,
                '€' + donation.amount.toFixed(2),
                donation.stripe_reference || '',
                donation.created_at ? new Date(donation.created_at).toLocaleDateString() : ''
            ]);
        });
        rows.push([]);
        
        // Auction Sales
        rows.push(['AUCTION SALES']);
        rows.push(['Item', 'Final Price', 'Sold At']);
        event.auction_sales.forEach(item => {
            rows.push([
                item.item_name,
                '€' + item.final_price.toFixed(2),
                item.sold_at ? new Date(item.sold_at).toLocaleDateString() : ''
            ]);
        });
        rows.push([]);
        
        // Costs
        rows.push(['COSTS']);
        rows.push(['Description', 'Amount', 'Added By', 'Date']);
        event.costs.forEach(cost => {
            rows.push([
                cost.description,
                '€' + cost.amount.toFixed(2),
                cost.added_by_name,
                cost.created_at ? new Date(cost.created_at).toLocaleDateString() : ''
            ]);
        });
        
        // Convert to CSV
        const csvContent = rows.map(row => row.map(cell => {
            const str = String(cell);
            return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
        }).join(',')).join('\n');
        
        // Generate filename: galeriesyess-SS26-finance-YYYY-MM-DD.csv
        const seasonTag = event.season_tag || 'EVENT';
        const today = new Date().toISOString().split('T')[0];
        const filename = `galeriesyess-${seasonTag}-finance-${today}.csv`;
        
        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        
        toast.success('Finance report exported');
    };

    const handleAddCost = async (e) => {
        e.preventDefault();
        if (!costForm.description || !costForm.amount || !selectedEventForCost) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setSubmitting(true);
        try {
            await api().post(`/admin/finance/costs/${selectedEventForCost}`, {
                description: costForm.description,
                amount: parseFloat(costForm.amount)
            });
            toast.success('Cost added');
            setShowAddCost(false);
            setCostForm({ description: '', amount: '' });
            // Refresh both overview and event details
            fetchFinanceData();
            if (selectedEventFinance === selectedEventForCost) {
                fetchEventFinanceData(selectedEventForCost);
            }
            setSelectedEventForCost(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add cost');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditCost = async (e) => {
        e.preventDefault();
        if (!costForm.description || !costForm.amount || !editingCost) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setSubmitting(true);
        try {
            await api().put(`/admin/finance/costs/${editingCost.id}`, {
                description: costForm.description,
                amount: parseFloat(costForm.amount)
            });
            toast.success('Cost updated');
            setShowEditCost(false);
            setCostForm({ description: '', amount: '' });
            setEditingCost(null);
            fetchFinanceData();
            if (selectedEventFinance) {
                fetchEventFinanceData(selectedEventFinance);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update cost');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditCost = (cost) => {
        setEditingCost(cost);
        setCostForm({ description: cost.description, amount: cost.amount.toString() });
        setShowEditCost(true);
    };

    const handleDeleteCost = async (costId) => {
        console.log('Delete cost clicked, costId:', costId);
        if (!window.confirm('Delete this cost entry?')) {
            console.log('Delete cancelled by user');
            return;
        }
        
        try {
            console.log('Sending delete request for cost:', costId);
            await api().delete(`/admin/finance/costs/${costId}`);
            toast.success('Cost deleted');
            fetchFinanceData();
            if (selectedEventFinance) {
                fetchEventFinanceData(selectedEventFinance);
            }
        } catch (error) {
            console.error('Delete cost error:', error);
            toast.error('Failed to delete cost');
        }
    };

    // QR Scan - lookup user by ID (from QR or manual entry)
    const handleLookupUser = async (userId) => {
        if (!userId) {
            toast.error('Please enter a user ID');
            return;
        }
        
        setProcessing(true);
        try {
            const eventId = currentEvent?.id || '';
            const response = await api().get(`/admin/scan/${userId}?event_id=${eventId}`);
            setScannedUser(response.data);
            setSwapCreditsInput(response.data.attendance?.swap_credits?.toString() || '0');
            setSwapItemsInput(''); // Reset for new check-in
            setCameraActive(false); // Close camera after successful scan
            toast.success(`Found: ${response.data.user.name}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'User not found');
            setScannedUser(null);
        } finally {
            setProcessing(false);
        }
    };

    // Handle QR code scan result
    const handleQrScan = (result) => {
        if (result?.text && !processing) {
            const scannedValue = result.text;
            // The QR code should contain the user ID
            handleLookupUser(scannedValue);
        }
    };

    // Handle QR scanner error
    const handleQrError = (error) => {
        console.error('QR Scanner error:', error);
        // Check for specific permission errors
        if (error?.name === 'NotAllowedError' || error?.message?.includes('Permission')) {
            setCameraError('Camera permission denied. Please allow camera access in your browser/app settings.');
        } else if (error?.name === 'NotFoundError') {
            setCameraError('No camera found on this device.');
        } else if (error?.name === 'NotReadableError') {
            setCameraError('Camera is in use by another application.');
        } else {
            setCameraError(error?.message || 'Camera access failed. Try refreshing the page.');
        }
    };

    // Request camera permission explicitly - improved for iOS
    const requestCameraPermission = async () => {
        try {
            // Check if mediaDevices is available
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setCameraError('Camera not supported on this device/browser. Try using Safari on iOS or Chrome on Android.');
                return;
            }
            
            // Request permission with iOS-friendly constraints
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { 
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } 
            });
            
            // Stop the stream immediately, we just wanted to request permission
            stream.getTracks().forEach(track => track.stop());
            setCameraError(null);
            setCameraActive(true);
        } catch (error) {
            console.error('Camera permission error:', error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setCameraError('Camera permission denied. On iOS: Settings → Safari → Camera → Allow. Then refresh the page.');
            } else if (error.name === 'NotFoundError') {
                setCameraError('No camera found. Please use a device with a camera.');
            } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
                setCameraError('Camera is busy or unavailable. Close other apps using the camera and try again.');
            } else if (error.name === 'OverconstrainedError') {
                // Try again with simpler constraints
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                    stream.getTracks().forEach(track => track.stop());
                    setCameraError(null);
                    setCameraActive(true);
                } catch (e) {
                    setCameraError('Camera access failed. Please try refreshing the page.');
                }
            } else {
                setCameraError(`Camera error: ${error.message || 'Unknown error'}. Try refreshing the page.`);
            }
        }
    };

    // Helper to determine if user is currently checked in (not checked out)
    const isUserCheckedIn = (attendance) => {
        if (!attendance?.checked_in_at) return false;
        if (!attendance.checked_out_at) return true;
        // If both exist, check which is more recent
        return new Date(attendance.checked_in_at) > new Date(attendance.checked_out_at);
    };

    // Check in user with swap items (physical items converted to swap credits)
    const handleCheckIn = async () => {
        if (!scannedUser || !currentEvent) return;
        
        const swapItems = parseInt(swapItemsInput) || 0;
        
        setProcessing(true);
        try {
            const response = await api().post(
                `/admin/attendance/${scannedUser.user.id}/check-in?event_id=${currentEvent.id}`,
                { swap_credits: swapItems }
            );
            setScannedUser(prev => ({
                ...prev,
                attendance: response.data.attendance
            }));
            setSwapCreditsInput(swapItems.toString());
            toast.success(`${scannedUser.user.name} checked in with ${swapItems} swap credits`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Check-in failed');
        } finally {
            setProcessing(false);
        }
    };

    // Check out user
    const handleCheckOut = async () => {
        if (!scannedUser || !currentEvent) return;
        
        setProcessing(true);
        try {
            const response = await api().post(
                `/admin/attendance/${scannedUser.user.id}/check-out?event_id=${currentEvent.id}`
            );
            setScannedUser(prev => ({
                ...prev,
                attendance: response.data.attendance
            }));
            setSwapCreditsInput('0');
            toast.success(`${scannedUser.user.name} checked out`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Check-out failed');
        } finally {
            setProcessing(false);
        }
    };

    // Update swap credits
    const handleUpdateSwapCredits = async () => {
        if (!scannedUser || !currentEvent) return;
        
        setProcessing(true);
        try {
            const response = await api().put(
                `/admin/attendance/${scannedUser.user.id}/swap-credits?event_id=${currentEvent.id}`,
                { swap_credits: parseInt(swapCreditsInput) || 0 }
            );
            setScannedUser(prev => ({
                ...prev,
                attendance: response.data.attendance
            }));
            toast.success('Swap credits updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Update failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        if (!eventForm.title || !eventForm.start_datetime || !eventForm.location_name) {
            toast.error('Please fill in required fields');
            return;
        }

        setSubmitting(true);
        try {
            await api().post('/events/', {
                ...eventForm,
                start_datetime: new Date(eventForm.start_datetime).toISOString(),
                end_datetime: new Date(eventForm.end_datetime || eventForm.start_datetime).toISOString(),
                ticket_price: parseFloat(eventForm.ticket_price) || 0,
                capacity: parseInt(eventForm.capacity) || 50,
            });
            toast.success('Event created!');
            setShowCreateEvent(false);
            setEventForm({
                title: '',
                season_tag: 'SS',
                start_datetime: '',
                end_datetime: '',
                location_name: '',
                address: '',
                program_text: '',
                ticket_price: 0,
                capacity: 50,
                is_open_access: false,
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateEvent = async (e) => {
        e.preventDefault();
        if (!editingEvent || !eventForm.title || !eventForm.start_datetime || !eventForm.location_name) {
            toast.error('Please fill in required fields');
            return;
        }

        setSubmitting(true);
        try {
            await api().put(`/events/${editingEvent.id}`, {
                ...eventForm,
                start_datetime: new Date(eventForm.start_datetime).toISOString(),
                end_datetime: new Date(eventForm.end_datetime || eventForm.start_datetime).toISOString(),
                ticket_price: parseFloat(eventForm.ticket_price) || 0,
                capacity: parseInt(eventForm.capacity) || 50,
            });
            toast.success('Event updated!');
            setShowCreateEvent(false);
            setEditingEvent(null);
            setEventForm({
                title: '',
                season_tag: 'SS',
                start_datetime: '',
                end_datetime: '',
                location_name: '',
                address: '',
                program_text: '',
                ticket_price: 0,
                capacity: 50,
                is_open_access: false,
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update event');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditEvent = (event) => {
        // Format dates for datetime-local input
        const formatForInput = (isoString) => {
            if (!isoString) return '';
            const date = new Date(isoString);
            return date.toISOString().slice(0, 16); // Format: "2026-03-21T13:00"
        };
        
        setEditingEvent(event);
        setEventForm({
            title: event.title || '',
            season_tag: event.season_tag || 'SS',
            start_datetime: formatForInput(event.start_datetime),
            end_datetime: formatForInput(event.end_datetime),
            location_name: event.location_name || '',
            address: event.address || '',
            program_text: event.program_text || '',
            ticket_price: event.ticket_price || 0,
            capacity: event.capacity || 50,
            is_open_access: event.is_open_access || false,
        });
        setShowCreateEvent(true);
    };

    const handleToggleStatus = async (eventId, newStatus) => {
        try {
            await api().post(`/events/${eventId}/status`, { status: newStatus });
            if (newStatus === 'live') {
                toast.success('🎉 Event is now LIVE! Ticket sales are open.');
            } else {
                toast.success('Event paused. Ticket sales closed.');
            }
            fetchData();
        } catch (error) {
            toast.error('Failed to update event status');
        }
    };

    const handleArchive = async (eventId) => {
        try {
            await api().post(`/events/${eventId}/archive`);
            toast.success('Event archived');
            fetchData();
        } catch (error) {
            toast.error('Failed to archive event');
        }
    };

    // Guest List functions
    const handleAddGuest = async (e) => {
        e.preventDefault();
        if (!guestForm.first_name || !guestForm.last_name || !guestForm.phone) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSubmitting(true);
        try {
            await api().post('/admin/guest-list', guestForm);
            toast.success(`${guestForm.first_name} added to guest list`);
            setShowAddGuest(false);
            setGuestForm({ first_name: '', last_name: '', phone: '', role: 'guest' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add guest');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveGuest = async (guestId) => {
        if (!window.confirm('Remove this guest from the list?')) return;
        
        try {
            await api().delete(`/admin/guest-list/${guestId}`);
            toast.success('Guest removed');
            fetchData();
        } catch (error) {
            toast.error('Failed to remove guest');
        }
    };

    // Invitation functions
    const fetchInvitationTemplate = async () => {
        try {
            const response = await api().get('/content/invitation');
            setInvitationTemplate(response.data);
        } catch (error) {
            console.error('Failed to fetch invitation template:', error);
        }
    };

    const saveInvitationTemplate = async () => {
        try {
            await api().put('/content/invitation', invitationTemplate);
            toast.success('Invitation template saved');
            setShowEditInvitation(false);
        } catch (error) {
            toast.error('Failed to save invitation template');
        }
    };

    const toggleGuestSelection = (guestId) => {
        setSelectedGuests(prev => 
            prev.includes(guestId) 
                ? prev.filter(id => id !== guestId)
                : [...prev, guestId]
        );
    };

    const selectAllFilteredGuests = () => {
        const filteredIds = getFilteredGuests().map(g => g.id);
        setSelectedGuests(filteredIds);
    };

    const clearSelection = () => {
        setSelectedGuests([]);
    };

    // Count invitations per admin
    const getInvitationCounts = () => {
        const hsiuLiCount = guestList.filter(g => g.invited_by_admin === '+31642829978').length;
        const jessicaCount = guestList.filter(g => g.invited_by_admin === '+31647136528').length;
        const notInvitedCount = guestList.filter(g => !g.invitation_sent).length;
        return { hsiuLiCount, jessicaCount, notInvitedCount };
    };

    const getFilteredGuests = () => {
        let filtered = [...guestList];
        
        // Sort alphabetically by first name
        filtered.sort((a, b) => {
            const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
            const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
        
        // Apply filter
        if (guestFilter === 'hsiu-li') {
            filtered = filtered.filter(g => g.invited_by_admin === '+31642829978');
        } else if (guestFilter === 'jessica') {
            filtered = filtered.filter(g => g.invited_by_admin === '+31647136528');
        } else if (guestFilter === 'not-invited') {
            filtered = filtered.filter(g => !g.invitation_sent);
        }
        
        return filtered;
    };

    const handleSendInvitations = async () => {
        if (selectedGuests.length === 0) {
            toast.error('Select guests to invite');
            return;
        }
        
        if (!invitationTemplate) {
            await fetchInvitationTemplate();
        }
        
        setShowInviteDialog(true);
    };

    const generateWhatsAppLink = (phone, message) => {
        // Remove + and spaces from phone number
        const cleanPhone = phone.replace(/[+\s]/g, '');
        const encodedMessage = encodeURIComponent(message);
        return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    };

    const markGuestsAsInvited = async () => {
        try {
            await api().post('/admin/guest-list/mark-invited-bulk', selectedGuests);
            toast.success(`Marked ${selectedGuests.length} guests as invited`);
            fetchData();
            setSelectedGuests([]);
            setShowInviteDialog(false);
        } catch (error) {
            toast.error('Failed to mark guests as invited');
        }
    };

    // View user details
    const handleViewUser = async (userId) => {
        setUserDetailLoading(true);
        setShowUserDetail(true);
        try {
            const response = await api().get(`/admin/users/${userId}`);
            setSelectedUser(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load user details');
            setShowUserDetail(false);
        } finally {
            setUserDetailLoading(false);
        }
    };

    // Delete user (admin only)
    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user? This will remove all their data including items, tickets, and attendance records.')) return;
        
        try {
            await api().delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            setShowUserDetail(false);
            setSelectedUser(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    const clearScannedUser = () => {
        setScannedUser(null);
        setManualUserId('');
        setSwapCreditsInput('');
        setSwapItemsInput('');
        setCameraActive(true); // Re-enable camera for next scan
        setCameraError(null);
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4 relative">
                    <button
                        onClick={() => navigate('/profile')}
                        className="absolute left-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img src={LOGO_URL} alt="Galeries Yess" className="h-8 w-auto" />
                </div>
            </div>

            <main className="pt-20 px-4 max-w-4xl mx-auto">
                <h1 className="text-2xl font-heading font-light tracking-tight mb-2">Admin Panel</h1>
                <p className="text-sm text-gray-400 mb-6">
                    {currentEvent ? `Current event: ${currentEvent.title}` : 'No active event'}
                </p>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {loading ? (
                        <>
                            <Skeleton className="h-24 bg-gray-100" />
                            <Skeleton className="h-24 bg-gray-100" />
                            <Skeleton className="h-24 bg-gray-100" />
                            <Skeleton className="h-24 bg-gray-100" />
                        </>
                    ) : (
                        <>
                            <div className="bg-white border border-gray-200 p-4" data-testid="stat-users">
                                <Users className="w-5 h-5 text-gray-400 mb-2" />
                                <p className="text-2xl font-mono font-bold">{stats?.total_users || 0}</p>
                                <p className="text-xs text-gray-400">Total Users</p>
                            </div>
                            <div className="bg-white border border-gray-200 p-4" data-testid="stat-items">
                                <Package className="w-5 h-5 text-gray-400 mb-2" />
                                <p className="text-2xl font-mono font-bold">{stats?.total_items || 0}</p>
                                <p className="text-xs text-gray-400">Auction Items</p>
                            </div>
                            <div className="bg-white border border-gray-200 p-4" data-testid="stat-sold">
                                <Package className="w-5 h-5 text-[#F8BBD6] mb-2" />
                                <p className="text-2xl font-mono font-bold">{stats?.items_sold || 0}</p>
                                <p className="text-xs text-gray-400">Items Sold</p>
                            </div>
                            <div className="bg-white border border-gray-200 p-4" data-testid="stat-revenue">
                                <DollarSign className="w-5 h-5 text-[#F8BBD6] mb-2" />
                                <p className="text-2xl font-mono font-bold">€{(stats?.total_revenue || 0).toFixed(0)}</p>
                                <p className="text-xs text-gray-400">Revenue</p>
                            </div>
                        </>
                    )}
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="overflow-x-auto -mx-6 px-6">
                        <TabsList className="inline-flex bg-white p-1 border border-gray-200 min-w-max gap-1">
                            <TabsTrigger value="scan" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-scan">
                                Scan
                            </TabsTrigger>
                            <TabsTrigger value="events" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-events">
                                Events ({events.length})
                            </TabsTrigger>
                            <TabsTrigger value="items" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-items">
                                Items ({adminItems.length})
                            </TabsTrigger>
                            <TabsTrigger value="news" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-news">
                                News
                            </TabsTrigger>
                            <TabsTrigger value="finance" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-finance">
                                Finance
                            </TabsTrigger>
                            <TabsTrigger value="guest-list" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-guest-list">
                                Guest List ({guestList.length})
                            </TabsTrigger>
                            <TabsTrigger value="users" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-users">
                                Users ({users.length})
                            </TabsTrigger>
                            <TabsTrigger value="pages" className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-black data-[state=active]:text-white" data-testid="admin-tab-pages">
                                Pages
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Scan Tab - Camera-first Check-in/Check-out */}
                    <TabsContent value="scan" className="mt-6">
                        <div className="space-y-6">
                            {!scannedUser ? (
                                <>
                                    {/* Camera Scanner Section */}
                                    <div className="bg-gray-50 border border-gray-200 p-4">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-heading font-medium flex items-center gap-2">
                                                <Camera className="w-5 h-5" />
                                                Scan QR Code
                                            </h3>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setCameraActive(!cameraActive)}
                                                data-testid="toggle-camera-btn"
                                            >
                                                {cameraActive ? 'Hide Camera' : 'Show Camera'}
                                            </Button>
                                        </div>
                                        
                                        {cameraActive && !cameraError && (
                                            <div className="relative aspect-square max-w-sm mx-auto rounded-lg overflow-hidden bg-black mb-4">
                                                <Suspense fallback={
                                                    <div className="flex items-center justify-center h-full">
                                                        <Loader2 className="w-8 h-8 animate-spin text-white" />
                                                    </div>
                                                }>
                                                    <QrReader
                                                        onResult={handleQrScan}
                                                        constraints={{ 
                                                            facingMode: 'environment',
                                                            width: { ideal: 1280 },
                                                            height: { ideal: 720 }
                                                        }}
                                                        containerStyle={{ width: '100%', height: '100%' }}
                                                        videoStyle={{ objectFit: 'cover' }}
                                                        scanDelay={500}
                                                        onError={handleQrError}
                                                        videoId="qr-video"
                                                    />
                                                </Suspense>
                                                {processing && (
                                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                        <Loader2 className="w-8 h-8 animate-spin text-white" />
                                                    </div>
                                                )}
                                                {/* Scanner overlay */}
                                                <div className="absolute inset-0 pointer-events-none">
                                                    <div className="absolute inset-8 border-2 border-white/50 rounded-lg"></div>
                                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-[#F8BBD6] rounded-lg"></div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {cameraError && (
                                            <div className="text-center py-8 bg-red-50 rounded-lg mb-4">
                                                <Camera className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                                <p className="text-sm text-red-600 mb-2">Camera unavailable</p>
                                                <p className="text-xs text-gray-500 mb-3">{cameraError}</p>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={requestCameraPermission}
                                                >
                                                    Request Camera Access
                                                </Button>
                                            </div>
                                        )}
                                        
                                        <p className="text-xs text-gray-400 text-center">
                                            Point camera at guest's QR code to scan
                                        </p>
                                    </div>

                                    {/* Manual Lookup Fallback */}
                                    <div className="bg-white border border-gray-200 p-4">
                                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                            <Search className="w-4 h-4" />
                                            Manual Lookup
                                        </h4>
                                        <div className="flex gap-2 mb-4">
                                            <Input
                                                placeholder="Enter user ID or search..."
                                                value={manualUserId}
                                                onChange={(e) => setManualUserId(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleLookupUser(manualUserId)}
                                                className="bg-gray-50"
                                                data-testid="user-id-input"
                                            />
                                            <Button
                                                onClick={() => handleLookupUser(manualUserId)}
                                                disabled={processing || !manualUserId}
                                                className="bg-black text-white"
                                                data-testid="lookup-user-btn"
                                            >
                                                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
                                            </Button>
                                        </div>
                                        
                                        {/* Quick select from registered users */}
                                        {users.length > 0 && (
                                            <div className="pt-3 border-t border-gray-100">
                                                <p className="text-xs text-gray-400 mb-2">Quick select:</p>
                                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                                    {users.map(u => (
                                                        <button
                                                            key={u.id}
                                                            onClick={() => handleLookupUser(u.id)}
                                                            className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 hover:border-black hover:bg-gray-100 transition-colors rounded"
                                                            data-testid={`quick-select-${u.id}`}
                                                        >
                                                            {u.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* User Found - Check-in/Check-out Flow */
                                <div className="bg-gray-50 border border-gray-200 p-6">
                                    {/* User Header */}
                                    <div className="flex items-start justify-between mb-6">
                                        <div>
                                            <h3 className="text-2xl font-heading">{scannedUser.user.name}</h3>
                                            <p className="text-sm text-gray-400">{scannedUser.user.phone}</p>
                                        </div>
                                        <button
                                            onClick={clearScannedUser}
                                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                                            data-testid="clear-user-btn"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    {/* Status Cards */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="bg-white p-4 border border-gray-200">
                                            <p className="text-xs text-gray-400 mb-1">Auction Items</p>
                                            <p className="text-2xl font-mono">{scannedUser.auction_items_count} / 5</p>
                                        </div>
                                        <div className="bg-white p-4 border border-gray-200">
                                            <p className="text-xs text-gray-400 mb-1">Status</p>
                                            <p className="text-lg font-medium">
                                                {isUserCheckedIn(scannedUser.attendance) ? (
                                                    <span className="text-green-600 flex items-center gap-1">
                                                        <UserCheck className="w-4 h-4" />
                                                        Checked In
                                                    </span>
                                                ) : scannedUser.attendance?.checked_out_at ? (
                                                    <span className="text-orange-500 flex items-center gap-1">
                                                        <UserMinus className="w-4 h-4" />
                                                        Checked Out
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">Not Checked In</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Check-in Flow - Not yet checked in or checked out */}
                                    {!isUserCheckedIn(scannedUser.attendance) ? (
                                        <div className="space-y-4">
                                            <div className="bg-white p-4 border border-gray-200 rounded-lg">
                                                <label className="text-sm font-medium mb-2 block">
                                                    Physical Swap Items Brought
                                                </label>
                                                <p className="text-xs text-gray-500 mb-3">
                                                    Count the physical items this guest is bringing for swap and enter the number below. These will be converted to swap credits.
                                                </p>
                                                <div className="flex gap-2 items-center">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        max="25"
                                                        placeholder="0"
                                                        value={swapItemsInput}
                                                        onChange={(e) => setSwapItemsInput(e.target.value)}
                                                        className="text-2xl font-mono bg-gray-50 w-24 text-center"
                                                        data-testid="swap-items-input"
                                                    />
                                                    <span className="text-gray-400">items → swap credits</span>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-2">Maximum 25 per event</p>
                                            </div>
                                            
                                            <Button
                                                onClick={handleCheckIn}
                                                disabled={processing}
                                                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                                                data-testid="check-in-btn"
                                            >
                                                {processing ? (
                                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                ) : (
                                                    <UserCheck className="w-5 h-5 mr-2" />
                                                )}
                                                Check In {swapItemsInput ? `with ${swapItemsInput} swap credits` : ''}
                                            </Button>
                                        </div>
                                    ) : (
                                        /* Check-out Flow - Already checked in */
                                        <div className="space-y-4">
                                            {/* Current Swap Credits Display */}
                                            <div className="bg-[#F8BBD6]/10 p-4 border border-[#F8BBD6] rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-1">Swap Credits</p>
                                                        <p className="text-3xl font-mono font-bold">{scannedUser.attendance?.swap_credits || 0}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500 mb-1">Used</p>
                                                        <p className="text-xl font-mono">{scannedUser.attendance?.swap_credits_used || 0}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Adjust Credits */}
                                            <div className="bg-white p-4 border border-gray-200 rounded-lg">
                                                <label className="text-sm font-medium mb-2 block">Adjust Swap Credits</label>
                                                <div className="flex gap-2 items-center">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        max="25"
                                                        value={swapCreditsInput}
                                                        onChange={(e) => setSwapCreditsInput(e.target.value)}
                                                        className="text-xl font-mono bg-gray-50 w-24 text-center"
                                                        data-testid="swap-credits-input"
                                                    />
                                                    <Button
                                                        onClick={handleUpdateSwapCredits}
                                                        disabled={processing}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            <Button
                                                onClick={handleCheckOut}
                                                disabled={processing}
                                                variant="outline"
                                                className="w-full border-red-300 text-red-600 hover:bg-red-50 py-6 text-lg"
                                                data-testid="check-out-btn"
                                            >
                                                {processing ? (
                                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                ) : (
                                                    <UserMinus className="w-5 h-5 mr-2" />
                                                )}
                                                Check Out
                                            </Button>
                                            <p className="text-xs text-gray-400 text-center">
                                                Check-out will reset swap credits to 0
                                            </p>
                                        </div>
                                    )}

                                    {/* Scan Another Button */}
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <Button
                                            onClick={clearScannedUser}
                                            variant="ghost"
                                            className="w-full"
                                            data-testid="scan-another-btn"
                                        >
                                            <Camera className="w-4 h-4 mr-2" />
                                            Scan Another Guest
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Events Tab */}
                    <TabsContent value="events" className="mt-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-heading font-medium">All Events</h3>
                            <Dialog open={showCreateEvent} onOpenChange={(open) => {
                                    setShowCreateEvent(open);
                                    if (!open) {
                                        setEditingEvent(null);
                                        setEventForm({
                                            title: '',
                                            season_tag: 'SS',
                                            start_datetime: '',
                                            end_datetime: '',
                                            location_name: '',
                                            address: '',
                                            program_text: '',
                                            ticket_price: 0,
                                            capacity: 50,
                                            is_open_access: false,
                                        });
                                    }
                                }}>
                                <DialogTrigger asChild>
                                    <Button className="bg-black text-white" data-testid="create-event-button">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Event
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="font-heading">{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="space-y-4 mt-4">
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Title *</label>
                                            <Input
                                                value={eventForm.title}
                                                onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                                                placeholder="Spring Auction 2026"
                                                data-testid="event-title-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Season</label>
                                            <Select
                                                value={eventForm.season_tag}
                                                onValueChange={(v) => setEventForm(prev => ({ ...prev, season_tag: v }))}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="SS">Spring/Summer</SelectItem>
                                                    <SelectItem value="FW">Fall/Winter</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Start *</label>
                                                <Input
                                                    type="datetime-local"
                                                    value={eventForm.start_datetime}
                                                    onChange={(e) => setEventForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                                                    data-testid="event-start-input"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">End</label>
                                                <Input
                                                    type="datetime-local"
                                                    value={eventForm.end_datetime}
                                                    onChange={(e) => setEventForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Location Name *</label>
                                            <Input
                                                value={eventForm.location_name}
                                                onChange={(e) => setEventForm(prev => ({ ...prev, location_name: e.target.value }))}
                                                placeholder="De Hallen, Amsterdam"
                                                data-testid="event-location-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Address</label>
                                            <Input
                                                value={eventForm.address}
                                                onChange={(e) => setEventForm(prev => ({ ...prev, address: e.target.value }))}
                                                placeholder="Hannie Dankbaarpassage 33"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Program</label>
                                            <Textarea
                                                value={eventForm.program_text}
                                                onChange={(e) => setEventForm(prev => ({ ...prev, program_text: e.target.value }))}
                                                placeholder="15:00 - Doors open&#10;16:00 - Auction starts"
                                                rows={3}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Ticket Price (€)</label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={eventForm.ticket_price}
                                                    onChange={(e) => setEventForm(prev => ({ ...prev, ticket_price: e.target.value }))}
                                                    data-testid="event-price-input"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Capacity</label>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={eventForm.capacity}
                                                    onChange={(e) => setEventForm(prev => ({ ...prev, capacity: e.target.value }))}
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full bg-black text-white"
                                            data-testid="submit-event-button"
                                        >
                                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingEvent ? 'Update Event' : 'Create Event')}
                                        </Button>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <div className="space-y-4">
                            {events.map(event => (
                                <div key={event.id} className="bg-white border border-gray-200 p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-heading font-medium">{event.title}</h4>
                                                <span className={`text-xs font-mono uppercase px-2 py-0.5 ${
                                                    event.status === 'live' ? 'bg-green-100 text-green-700' :
                                                    event.status === 'archived' ? 'bg-gray-100 text-gray-500' :
                                                    'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {event.status}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-400">
                                                {format(new Date(event.start_datetime), 'PPP')} · {event.location_name}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            {/* Edit Button - always visible for non-archived events */}
                                            {event.status !== 'archived' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openEditEvent(event)}
                                                    data-testid={`edit-event-${event.id}`}
                                                >
                                                    <Pencil className="w-4 h-4 mr-1" />
                                                    Edit
                                                </Button>
                                            )}
                                            {event.status === 'upcoming' && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleToggleStatus(event.id, 'live')}
                                                    className="bg-green-600 hover:bg-green-700 text-white"
                                                    data-testid={`go-live-${event.id}`}
                                                >
                                                    <Play className="w-4 h-4 mr-1" />
                                                    Go Live
                                                </Button>
                                            )}
                                            {event.status === 'live' && (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleToggleStatus(event.id, 'upcoming')}
                                                        data-testid={`pause-${event.id}`}
                                                    >
                                                        Pause Sale
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleArchive(event.id)}
                                                        data-testid={`archive-${event.id}`}
                                                    >
                                                        <Archive className="w-4 h-4 mr-1" />
                                                        Archive
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Items Tab - Auction Item Management */}
                    <TabsContent value="items" className="mt-6">
                        {itemsLoading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="animate-pulse">
                                    <svg className="w-8 h-8 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                    </svg>
                                </div>
                                <p className="text-xs text-gray-400 mt-3">Loading...</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading font-medium">Auction Items</h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => fetchAdminItems()}
                                        data-testid="refresh-items"
                                    >
                                        Refresh
                                    </Button>
                                </div>
                                
                                {adminItems.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500">No items yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {adminItems.map(item => (
                                            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center gap-4">
                                                    {/* Item Image */}
                                                    <div className="w-16 h-16 rounded bg-gray-100 overflow-hidden flex-shrink-0">
                                                        {item.images?.[0] ? (
                                                            <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <Package className="w-6 h-6 text-gray-300" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Item Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-medium truncate">{item.title}</h4>
                                                        <p className="text-sm text-gray-500">{item.owner_name}</p>
                                                        <p className="text-sm font-mono">€{item.price?.toFixed(2) || '0.00'}</p>
                                                    </div>
                                                    
                                                    {/* Status & Actions */}
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs px-2 py-1 rounded ${
                                                            item.status === 'sold' ? 'bg-green-100 text-green-700' :
                                                            item.status === 'unsold' ? 'bg-gray-100 text-gray-500' :
                                                            item.status === 'available' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {item.status}
                                                        </span>
                                                        
                                                        {item.status !== 'sold' && item.status !== 'unsold' && (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedItemForStatus(item);
                                                                    setItemStatusForm({ status: 'sold', final_price: item.price?.toString() || '' });
                                                                    setShowItemStatusModal(true);
                                                                }}
                                                                data-testid={`update-status-${item.id}`}
                                                            >
                                                                Update
                                                            </Button>
                                                        )}
                                                        
                                                        {(item.status === 'sold' || item.status === 'unsold') && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setSelectedItemForStatus(item);
                                                                    setItemStatusForm({ status: 'available', final_price: '' });
                                                                    setShowItemStatusModal(true);
                                                                }}
                                                                data-testid={`reopen-${item.id}`}
                                                            >
                                                                Reopen
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    {/* Item Status Modal */}
                    <Dialog open={showItemStatusModal} onOpenChange={setShowItemStatusModal}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">
                                    {itemStatusForm.status === 'available' ? 'Reopen Item' : 'Update Item Status'}
                                </DialogTitle>
                            </DialogHeader>
                            {selectedItemForStatus && (
                                <form onSubmit={handleUpdateItemStatus} className="space-y-4 mt-4">
                                    <div className="bg-gray-50 p-3 rounded">
                                        <p className="font-medium">{selectedItemForStatus.title}</p>
                                        <p className="text-sm text-gray-500">by {selectedItemForStatus.owner_name}</p>
                                    </div>
                                    
                                    {itemStatusForm.status !== 'available' && (
                                        <>
                                            <div>
                                                <label className="text-sm font-medium mb-2 block">Mark as</label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        variant={itemStatusForm.status === 'sold' ? 'default' : 'outline'}
                                                        className={itemStatusForm.status === 'sold' ? 'bg-green-600 hover:bg-green-700' : ''}
                                                        onClick={() => setItemStatusForm(prev => ({ ...prev, status: 'sold' }))}
                                                    >
                                                        Sold
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant={itemStatusForm.status === 'unsold' ? 'default' : 'outline'}
                                                        className={itemStatusForm.status === 'unsold' ? 'bg-gray-600 hover:bg-gray-700' : ''}
                                                        onClick={() => setItemStatusForm(prev => ({ ...prev, status: 'unsold' }))}
                                                    >
                                                        Unsold
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {itemStatusForm.status === 'sold' && (
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">Final Sale Price (€) *</label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={itemStatusForm.final_price}
                                                        onChange={(e) => setItemStatusForm(prev => ({ ...prev, final_price: e.target.value }))}
                                                        placeholder="0.00"
                                                        data-testid="final-price-input"
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        className="w-full bg-black text-white"
                                    >
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 
                                            itemStatusForm.status === 'available' ? 'Reopen Item' : 
                                            itemStatusForm.status === 'sold' ? 'Mark as Sold' : 'Mark as Unsold'
                                        }
                                    </Button>
                                </form>
                            )}
                        </DialogContent>
                    </Dialog>

                    {/* News Tab - Article Management */}
                    <TabsContent value="news" className="mt-6">
                        <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <h3 className="font-heading font-medium">News Articles</h3>
                                <Button
                                    onClick={() => setShowArticleForm(true)}
                                    size="sm"
                                    className="bg-black text-white hover:bg-gray-800"
                                    data-testid="new-article-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    New Article
                                </Button>
                            </div>

                            {/* Article Form */}
                            {showArticleForm && (
                                <div className="border border-gray-200 p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="font-medium">{editingArticle ? 'Edit Article' : 'New Article'}</h4>
                                        <Button variant="ghost" size="sm" onClick={resetArticleForm}>
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    <form onSubmit={handleCreateArticle} className="space-y-4">
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Title *</label>
                                            <Input
                                                value={articleForm.title}
                                                onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                                                placeholder="Article title"
                                                data-testid="article-title-input"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Subtitle</label>
                                            <Input
                                                value={articleForm.subtitle}
                                                onChange={(e) => setArticleForm(prev => ({ ...prev, subtitle: e.target.value }))}
                                                placeholder="Optional subtitle"
                                                data-testid="article-subtitle-input"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Category</label>
                                            <Select
                                                value={articleForm.category}
                                                onValueChange={(value) => setArticleForm(prev => ({ ...prev, category: value }))}
                                            >
                                                <SelectTrigger data-testid="article-category-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ARTICLE_CATEGORIES.map(cat => (
                                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Content</label>
                                            <Textarea
                                                value={articleForm.content}
                                                onChange={(e) => setArticleForm(prev => ({ ...prev, content: e.target.value }))}
                                                placeholder="Article content (HTML supported: <b>, <i>, <a>, <p>)"
                                                rows={8}
                                                data-testid="article-content-input"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Supports basic HTML: &lt;b&gt;, &lt;i&gt;, &lt;a href&gt;, &lt;p&gt;</p>
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Header Image</label>
                                            <div className="mt-1">
                                                {articleForm.header_image ? (
                                                    <div className="relative">
                                                        <img 
                                                            src={articleForm.header_image} 
                                                            alt="Header" 
                                                            className="w-full h-48 object-cover rounded"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="destructive"
                                                            size="sm"
                                                            className="absolute top-2 right-2"
                                                            onClick={() => setArticleForm(prev => ({ ...prev, header_image: '' }))}
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-gray-400">
                                                        <div className="text-center">
                                                            <ImageIcon className="w-8 h-8 mx-auto text-gray-400" />
                                                            <span className="text-sm text-gray-500">Upload header image</span>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={(e) => handleArticleImageUpload(e, 'header')}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="text-xs text-gray-500 uppercase tracking-wider">Inline Images (max 3)</label>
                                            <div className="grid grid-cols-3 gap-2 mt-1">
                                                {articleForm.inline_images.map((img, index) => (
                                                    <div key={index} className="relative">
                                                        <img 
                                                            src={img} 
                                                            alt={`Inline ${index + 1}`} 
                                                            className="w-full h-24 object-cover rounded"
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="destructive"
                                                            size="sm"
                                                            className="absolute top-1 right-1 h-6 w-6 p-0"
                                                            onClick={() => removeInlineImage(index)}
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                ))}
                                                {articleForm.inline_images.length < 3 && (
                                                    <label className="flex items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-gray-400">
                                                        <Plus className="w-6 h-6 text-gray-400" />
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={(e) => handleArticleImageUpload(e, 'inline')}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="is_published"
                                                checked={articleForm.is_published}
                                                onChange={(e) => setArticleForm(prev => ({ ...prev, is_published: e.target.checked }))}
                                                className="rounded"
                                            />
                                            <label htmlFor="is_published" className="text-sm">Publish immediately</label>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <Button
                                                type="submit"
                                                disabled={submitting}
                                                className="flex-1 bg-black text-white hover:bg-gray-800"
                                            >
                                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                {editingArticle ? 'Save Changes' : (articleForm.is_published ? 'Publish Article' : 'Save as Draft')}
                                            </Button>
                                            <Button type="button" variant="outline" onClick={resetArticleForm}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Articles List - hide editing article */}
                            <div className="space-y-3">
                                {articles.length === 0 ? (
                                    <p className="text-center text-gray-400 py-8 italic">No articles yet</p>
                                ) : (
                                    articles
                                        .filter(article => !editingArticle || article.id !== editingArticle.id)
                                        .map((article) => (
                                        <div key={article.id} className="border border-gray-200 p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                            article.is_published 
                                                                ? 'bg-green-100 text-green-700' 
                                                                : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {article.is_published ? 'Published' : 'Draft'}
                                                        </span>
                                                        <span className="text-xs text-gray-400">{article.category}</span>
                                                    </div>
                                                    <h4 className="font-medium truncate">{article.title}</h4>
                                                    {article.subtitle && (
                                                        <p className="text-sm text-gray-500 truncate">{article.subtitle}</p>
                                                    )}
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {article.created_at && format(new Date(article.created_at), 'MMM d, yyyy')}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleTogglePublish(article)}
                                                        title={article.is_published ? 'Unpublish' : 'Publish'}
                                                    >
                                                        {article.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditArticle(article)}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteArticle(article.id)}
                                                        className="text-red-500 hover:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Finance Tab - Executive Overview */}
                    <TabsContent value="finance" className="mt-6">
                        {financeLoading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-12 bg-gray-100" />
                                <Skeleton className="h-64 bg-gray-100" />
                            </div>
                        ) : financeData ? (
                            <div className="space-y-6">
                                {/* Event Selector */}
                                {financeData.events.length > 0 && (
                                    <div className="flex items-center gap-4">
                                        <Select
                                            value={selectedEventFinance || ''}
                                            onValueChange={(v) => setSelectedEventFinance(v)}
                                        >
                                            <SelectTrigger className="w-full bg-white" data-testid="finance-event-select">
                                                <SelectValue placeholder="Select event" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {financeData.events.map(event => (
                                                    <SelectItem key={event.event_id} value={event.event_id}>
                                                        {event.event_title}
                                                        {event.event_status === 'live' && ' (Live)'}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* Event Finance Details */}
                                {eventFinanceLoading ? (
                                    <div className="space-y-4">
                                        <Skeleton className="h-48 bg-gray-100" />
                                        <Skeleton className="h-32 bg-gray-100" />
                                    </div>
                                ) : eventFinanceData ? (
                                    <div className="space-y-6">
                                        {/* EXECUTIVE SUMMARY - Always Visible */}
                                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                                            <div className="space-y-4">
                                                {/* Revenue Lines */}
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between py-2">
                                                        <span className="text-gray-600">Ticket Revenue</span>
                                                        <span className="font-mono">€{eventFinanceData.ticket_revenue?.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between py-2">
                                                        <span className="text-gray-600">Auction Revenue</span>
                                                        <span className="font-mono">€{eventFinanceData.auction_revenue?.toFixed(2)}</span>
                                                    </div>
                                                    
                                                    {/* Subtotal */}
                                                    <div className="flex items-center justify-between py-2 border-t border-gray-100">
                                                        <span className="font-medium">Total Revenue</span>
                                                        <span className="font-mono font-medium text-green-700">€{eventFinanceData.total_revenue?.toFixed(2)}</span>
                                                    </div>
                                                    
                                                    {/* Costs */}
                                                    <div className="flex items-center justify-between py-2">
                                                        <span className="text-gray-600">Total Costs</span>
                                                        <span className="font-mono text-red-600">-€{eventFinanceData.total_costs?.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                
                                                {/* Net Donation - Emphasized */}
                                                <div className="bg-[#FCE7F0]/50 rounded-lg p-4 mt-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-heading font-medium text-lg">Net Donation Amount</p>
                                                            <p className="text-xs text-gray-500 mt-1">Total revenue minus recorded costs</p>
                                                        </div>
                                                        <p className={`text-3xl font-mono font-bold ${eventFinanceData.net_donation >= 0 ? 'text-[#e91e8c]' : 'text-red-500'}`}>
                                                            €{eventFinanceData.net_donation?.toFixed(2)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* EXPANDABLE DETAIL SECTIONS */}
                                        <div className="space-y-2">
                                            {/* Ticket Revenue Details */}
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <button
                                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                                    onClick={() => toggleSection('tickets')}
                                                    data-testid="toggle-tickets"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Ticket className="w-5 h-5 text-gray-400" />
                                                        <span className="font-medium">Ticket Revenue</span>
                                                        <span className="text-xs text-gray-400">({eventFinanceData.ticket_count} tickets)</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono">€{eventFinanceData.ticket_revenue?.toFixed(2)}</span>
                                                        {expandedSections.tickets ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                    </div>
                                                </button>
                                                {expandedSections.tickets && (
                                                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                                                        {eventFinanceData.ticket_sales?.length > 0 ? (
                                                            <div className="space-y-2">
                                                                <div className="grid grid-cols-5 text-xs text-gray-400 pb-2 border-b border-gray-200">
                                                                    <span>Buyer</span>
                                                                    <span className="text-center">Tickets</span>
                                                                    <span className="text-right">Amount</span>
                                                                    <span className="text-right">Confirmed</span>
                                                                    <span className="text-right">Action</span>
                                                                </div>
                                                                {eventFinanceData.ticket_sales.map((sale, idx) => (
                                                                    <div key={idx} className="grid grid-cols-5 text-sm py-2 border-b border-gray-100 last:border-0 items-center">
                                                                        <span className="truncate">{sale.buyer_name}</span>
                                                                        <span className="text-center">{sale.ticket_count}</span>
                                                                        <span className="text-right font-mono">€{sale.total_amount?.toFixed(2)}</span>
                                                                        <span className="text-right">{sale.payment_confirmed ? '✓' : '—'}</span>
                                                                        <span className="text-right">
                                                                            <button
                                                                                onClick={async () => {
                                                                                    if (!window.confirm(`${sale.buyer_name} ticket(s) verwijderen?`)) return;
                                                                                    try {
                                                                                        await api().delete(`/admin/finance/tickets/user/${sale.user_id}`);
                                                                                        toast.success('Ticket(s) verwijderd');
                                                                                        fetchEventFinanceData(selectedEventFinance);
                                                                                        fetchFinanceData();
                                                                                    } catch (err) {
                                                                                        toast.error('Kon niet verwijderen');
                                                                                    }
                                                                                }}
                                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No ticket sales yet</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Donation Revenue Details */}
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <button
                                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                                    onClick={() => toggleSection('donations')}
                                                    data-testid="toggle-donations"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Heart className="w-5 h-5 text-gray-400" />
                                                        <span className="font-medium">Donation Revenue</span>
                                                        <span className="text-xs text-gray-400">({eventFinanceData.donation_count} donations)</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono">€{eventFinanceData.donation_revenue?.toFixed(2)}</span>
                                                        {expandedSections.donations ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                    </div>
                                                </button>
                                                {expandedSections.donations && (
                                                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                                                        {/* Add manual donation button */}
                                                        <div className="flex justify-end mb-3">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => setShowAddDonation(true)}
                                                                className="text-xs"
                                                            >
                                                                <Plus className="w-3 h-3 mr-1" />
                                                                Donatie toevoegen
                                                            </Button>
                                                        </div>
                                                        {eventFinanceData.donations?.length > 0 ? (
                                                            <div className="space-y-2">
                                                                <div className="grid grid-cols-4 text-xs text-gray-400 pb-2 border-b border-gray-200">
                                                                    <span>Donor</span>
                                                                    <span className="text-right">Amount</span>
                                                                    <span className="text-right">Note</span>
                                                                    <span className="text-right">Action</span>
                                                                </div>
                                                                {eventFinanceData.donations.map((donation, idx) => (
                                                                    <div key={idx} className="grid grid-cols-4 text-sm py-2 border-b border-gray-100 last:border-0 items-center">
                                                                        <span className="truncate">{donation.donor_name || 'Anoniem'}</span>
                                                                        <span className="text-right font-mono">€{donation.amount?.toFixed(2)}</span>
                                                                        <span className="text-right text-xs text-gray-400 truncate">{donation.note || '—'}</span>
                                                                        <span className="text-right">
                                                                            <button
                                                                                onClick={async () => {
                                                                                    if (!window.confirm('Donatie verwijderen?')) return;
                                                                                    try {
                                                                                        await api().delete(`/admin/finance/donations/${donation.id}`);
                                                                                        toast.success('Donatie verwijderd');
                                                                                        fetchEventFinanceData(selectedEventFinance);
                                                                                        fetchFinanceData();
                                                                                    } catch (err) {
                                                                                        toast.error('Kon niet verwijderen');
                                                                                    }
                                                                                }}
                                                                                className="text-red-500 hover:text-red-700"
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No donations yet</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Auction Revenue Details */}
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <button
                                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                                    onClick={() => toggleSection('auction')}
                                                    data-testid="toggle-auction"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <ShoppingBag className="w-5 h-5 text-gray-400" />
                                                        <span className="font-medium">Auction Revenue</span>
                                                        <span className="text-xs text-gray-400">({eventFinanceData.items_sold_count} items sold)</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono">€{eventFinanceData.auction_revenue?.toFixed(2)}</span>
                                                        {expandedSections.auction ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                    </div>
                                                </button>
                                                {expandedSections.auction && (
                                                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                                                        {eventFinanceData.auction_sales?.length > 0 ? (
                                                            <div className="space-y-2">
                                                                <div className="grid grid-cols-3 text-xs text-gray-400 pb-2 border-b border-gray-200">
                                                                    <span>Item</span>
                                                                    <span className="text-right">Final Price</span>
                                                                    <span className="text-right">Sold At</span>
                                                                </div>
                                                                {eventFinanceData.auction_sales.map((item, idx) => (
                                                                    <div key={idx} className="grid grid-cols-3 text-sm py-2 border-b border-gray-100 last:border-0">
                                                                        <span className="truncate">{item.item_name}</span>
                                                                        <span className="text-right font-mono">€{item.final_price?.toFixed(2)}</span>
                                                                        <span className="text-right text-xs text-gray-400">
                                                                            {item.sold_at ? format(new Date(item.sold_at), 'MMM d') : '—'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No items sold yet</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Costs Details */}
                                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                <button
                                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                                    onClick={() => toggleSection('costs')}
                                                    data-testid="toggle-costs"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <DollarSign className="w-5 h-5 text-gray-400" />
                                                        <span className="font-medium">Costs</span>
                                                        <span className="text-xs text-gray-400">({eventFinanceData.cost_count} entries)</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-red-600">-€{eventFinanceData.total_costs?.toFixed(2)}</span>
                                                        {expandedSections.costs ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                                    </div>
                                                </button>
                                                {expandedSections.costs && (
                                                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                                                        {eventFinanceData.costs?.length > 0 ? (
                                                            <div className="space-y-2">
                                                                <div className="grid grid-cols-5 text-xs text-gray-400 pb-2 border-b border-gray-200">
                                                                    <span className="col-span-2">Description</span>
                                                                    <span className="text-right">Amount</span>
                                                                    <span className="text-right">Added</span>
                                                                    <span className="text-right">Actions</span>
                                                                </div>
                                                                {eventFinanceData.costs.map((cost) => (
                                                                    <div key={cost.id} className="grid grid-cols-5 text-sm py-2 border-b border-gray-100 last:border-0 items-center">
                                                                        <div className="col-span-2">
                                                                            <p className="truncate">{cost.description}</p>
                                                                            <p className="text-xs text-gray-400">{cost.added_by_name}</p>
                                                                        </div>
                                                                        <span className="text-right font-mono text-red-600">-€{cost.amount?.toFixed(2)}</span>
                                                                        <span className="text-right text-xs text-gray-400">
                                                                            {cost.created_at ? format(new Date(cost.created_at), 'MMM d') : '—'}
                                                                        </span>
                                                                        <div className="text-right flex items-center justify-end gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    openEditCost(cost);
                                                                                }}
                                                                                className="text-gray-500 hover:text-black p-2 hover:bg-gray-100 rounded"
                                                                                title="Edit cost"
                                                                                data-testid={`edit-cost-${cost.id}`}
                                                                            >
                                                                                <Pencil className="w-4 h-4" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    handleDeleteCost(cost.id);
                                                                                }}
                                                                                className="text-red-400 hover:text-red-600 p-2 hover:bg-red-50 rounded"
                                                                                title="Delete cost"
                                                                                data-testid={`delete-cost-${cost.id}`}
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No costs recorded yet</p>
                                                        )}
                                                        
                                                        {/* Add Cost Button */}
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="mt-4 w-full"
                                                            onClick={() => {
                                                                setSelectedEventForCost(eventFinanceData.event_id);
                                                                setCostForm({ description: '', amount: '' });
                                                                setShowAddCost(true);
                                                            }}
                                                            data-testid="add-cost-button"
                                                        >
                                                            <Plus className="w-4 h-4 mr-2" />
                                                            Add Cost
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Export Button */}
                                        <Button
                                            variant="outline"
                                            onClick={exportFinanceCSV}
                                            className="w-full"
                                            data-testid="export-finance-csv"
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Export Finance CSV
                                        </Button>
                                    </div>
                                ) : financeData.events.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500">No events yet</p>
                                        <p className="text-sm text-gray-400">Create an event to start tracking finances</p>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-400">
                                        Select an event to view finance details
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-gray-400">Failed to load finance data</p>
                                <Button onClick={fetchFinanceData} variant="outline" className="mt-2">
                                    Retry
                                </Button>
                            </div>
                        )}
                    </TabsContent>

                    {/* Add Cost Dialog */}
                    <Dialog open={showAddCost} onOpenChange={setShowAddCost}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Add Cost</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddCost} className="space-y-4 mt-4">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Description *</label>
                                    <Input
                                        value={costForm.description}
                                        onChange={(e) => setCostForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="e.g. Venue rental, catering, decoration..."
                                        data-testid="cost-description"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Amount (€) *</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={costForm.amount}
                                        onChange={(e) => setCostForm(prev => ({ ...prev, amount: e.target.value }))}
                                        placeholder="0.00"
                                        data-testid="cost-amount"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-black text-white"
                                    data-testid="submit-cost-button"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Edit Cost Dialog */}
                    <Dialog open={showEditCost} onOpenChange={(open) => {
                        setShowEditCost(open);
                        if (!open) {
                            setEditingCost(null);
                            setCostForm({ description: '', amount: '' });
                        }
                    }}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Edit Cost</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleEditCost} className="space-y-4 mt-4">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Description *</label>
                                    <Input
                                        value={costForm.description}
                                        onChange={(e) => setCostForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="e.g. Venue rental, catering, decoration..."
                                        data-testid="edit-cost-description"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Amount (€) *</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={costForm.amount}
                                        onChange={(e) => setCostForm(prev => ({ ...prev, amount: e.target.value }))}
                                        placeholder="0.00"
                                        data-testid="edit-cost-amount"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-black text-white"
                                    data-testid="update-cost-button"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Guest List Tab */}
                    <TabsContent value="guest-list" className="mt-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                            <h3 className="font-heading font-medium">Guest List ({guestList.length})</h3>
                            <div className="flex gap-2 flex-wrap">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                        fetchInvitationTemplate();
                                        setShowEditInvitation(true);
                                    }}
                                >
                                    Edit Invitation
                                </Button>
                                <Dialog open={showAddGuest} onOpenChange={setShowAddGuest}>
                                    <DialogTrigger asChild>
                                        <Button className="bg-black text-white" size="sm" data-testid="add-guest-button">
                                            <Plus className="w-4 h-4 mr-2" />
                                            Add Guest
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle className="font-heading">Add Guest</DialogTitle>
                                        </DialogHeader>
                                        <form onSubmit={handleAddGuest} className="space-y-4 mt-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">First Name *</label>
                                                    <Input
                                                        value={guestForm.first_name}
                                                        onChange={(e) => setGuestForm(prev => ({ ...prev, first_name: e.target.value }))}
                                                        placeholder="Anna"
                                                        data-testid="guest-first-name"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium mb-1 block">Last Name *</label>
                                                    <Input
                                                        value={guestForm.last_name}
                                                        onChange={(e) => setGuestForm(prev => ({ ...prev, last_name: e.target.value }))}
                                                        placeholder="de Vries"
                                                        data-testid="guest-last-name"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Phone Number *</label>
                                                <Input
                                                    value={guestForm.phone}
                                                    onChange={(e) => setGuestForm(prev => ({ ...prev, phone: e.target.value }))}
                                                    placeholder="+31612345678"
                                                    data-testid="guest-phone"
                                                />
                                                <p className="text-xs text-gray-400 mt-1">Include country code (e.g., +31)</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium mb-1 block">Role</label>
                                                <Select
                                                    value={guestForm.role}
                                                    onValueChange={(v) => setGuestForm(prev => ({ ...prev, role: v }))}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="guest">Guest</SelectItem>
                                                        <SelectItem value="admin">Admin</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <Button
                                                type="submit"
                                                disabled={submitting}
                                                className="w-full bg-black text-white"
                                                data-testid="submit-guest-button"
                                            >
                                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Guest List'}
                                            </Button>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </div>

                        {/* Filter and Selection Controls */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <Select value={guestFilter} onValueChange={setGuestFilter}>
                                <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Filter by..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Guests ({guestList.length})</SelectItem>
                                    <SelectItem value="hsiu-li">Hsiu-Li ({getInvitationCounts().hsiuLiCount})</SelectItem>
                                    <SelectItem value="jessica">Jessica ({getInvitationCounts().jessicaCount})</SelectItem>
                                    <SelectItem value="not-invited">Not Yet Invited ({getInvitationCounts().notInvitedCount})</SelectItem>
                                </SelectContent>
                            </Select>
                            
                            {selectedGuests.length > 0 ? (
                                <>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={clearSelection}
                                    >
                                        Clear ({selectedGuests.length})
                                    </Button>
                                    <Button 
                                        className="bg-green-600 text-white"
                                        size="sm"
                                        onClick={handleSendInvitations}
                                    >
                                        Send Invitations ({selectedGuests.length})
                                    </Button>
                                </>
                            ) : (
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={selectAllFilteredGuests}
                                >
                                    Select All
                                </Button>
                            )}
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            Only phone numbers on this list can log in via OTP. Click to select guests for bulk invitation.
                        </p>

                        <div className="space-y-2">
                            {getFilteredGuests().map(guest => (
                                <div
                                    key={guest.id}
                                    className={`flex items-center justify-between bg-white border p-3 cursor-pointer transition-colors ${
                                        selectedGuests.includes(guest.id) 
                                            ? 'border-black bg-gray-50' 
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                    onClick={() => toggleGuestSelection(guest.id)}
                                    data-testid={`guest-item-${guest.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Checkbox 
                                            checked={selectedGuests.includes(guest.id)}
                                            onCheckedChange={() => toggleGuestSelection(guest.id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                            guest.is_registered ? 'bg-green-100' : 'bg-gray-100'
                                        }`}>
                                            <span className={`text-sm font-medium ${
                                                guest.is_registered ? 'text-green-600' : 'text-gray-400'
                                            }`}>
                                                {guest.first_name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium">{guest.first_name} {guest.last_name}</p>
                                            <p className="text-xs text-gray-400">{guest.phone}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {guest.invitation_sent && (
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1">
                                                Invited
                                            </span>
                                        )}
                                        <span className={`text-xs px-2 py-1 ${
                                            guest.is_registered ? 'bg-green-100 text-green-700' : 
                                            'bg-gray-100 text-gray-500'
                                        }`}>
                                            {guest.is_registered ? 'Registered' : 'Pending'}
                                        </span>
                                        {guest.role === 'admin' && (
                                            <span className="text-xs bg-black text-white px-2 py-1">
                                                Admin
                                            </span>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveGuest(guest.id);
                                            }}
                                            className="p-1 hover:bg-red-50 text-red-500 rounded transition-colors"
                                            data-testid={`remove-guest-${guest.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {getFilteredGuests().length === 0 && (
                                <p className="text-gray-400 text-center py-8 italic">
                                    {guestFilter === 'all' 
                                        ? 'No guests on the list yet. Add guests to allow them to log in.'
                                        : 'No guests match this filter.'}
                                </p>
                            )}
                        </div>

                        {/* Send Invitations Dialog */}
                        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="font-heading">Send Invitations</DialogTitle>
                                </DialogHeader>
                                <div className="mt-4 space-y-4">
                                    <p className="text-sm text-gray-600">
                                        You have selected <strong>{selectedGuests.length}</strong> guests. 
                                        Click on each WhatsApp link to send the invitation individually.
                                    </p>
                                    
                                    {invitationTemplate && (
                                        <div className="bg-gray-50 p-3 rounded text-sm max-h-40 overflow-y-auto">
                                            <p className="whitespace-pre-wrap">{invitationTemplate.message?.replace('{app_link}', 'https://app.galeriesyess.com')}</p>
                                        </div>
                                    )}

                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {selectedGuests.map(guestId => {
                                            const guest = guestList.find(g => g.id === guestId);
                                            if (!guest) return null;
                                            const whatsappLink = generateWhatsAppLink(
                                                guest.phone, 
                                                invitationTemplate?.message?.replace('{app_link}', 'https://app.galeriesyess.com') || ''
                                            );
                                            return (
                                                <div key={guestId} className="flex items-center justify-between bg-white border border-gray-200 p-2 rounded">
                                                    <span className="text-sm">{guest.first_name} {guest.last_name}</span>
                                                    <a 
                                                        href={whatsappLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-green-500 text-white text-xs px-3 py-1 rounded hover:bg-green-600"
                                                    >
                                                        WhatsApp
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <Button 
                                        onClick={markGuestsAsInvited}
                                        className="w-full bg-black text-white"
                                    >
                                        Mark All as Invited
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Edit Invitation Template Dialog */}
                        <Dialog open={showEditInvitation} onOpenChange={setShowEditInvitation}>
                            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="font-heading">Edit Invitation Template</DialogTitle>
                                </DialogHeader>
                                {invitationTemplate && (
                                    <div className="mt-4 space-y-4">
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Title</label>
                                            <Input
                                                value={invitationTemplate.title || ''}
                                                onChange={(e) => setInvitationTemplate(prev => ({ ...prev, title: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-1 block">Message</label>
                                            <textarea
                                                value={invitationTemplate.message || ''}
                                                onChange={(e) => setInvitationTemplate(prev => ({ ...prev, message: e.target.value }))}
                                                className="w-full h-64 p-3 border border-gray-200 rounded text-sm"
                                                placeholder="Enter invitation message. Use {app_link} for the app link."
                                            />
                                            <p className="text-xs text-gray-400 mt-1">
                                                Use <code className="bg-gray-100 px-1">{'{app_link}'}</code> where you want the app link to appear.
                                            </p>
                                        </div>
                                        <Button 
                                            onClick={saveInvitationTemplate}
                                            className="w-full bg-black text-white"
                                        >
                                            Save Template
                                        </Button>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users" className="mt-6">
                        <h3 className="font-heading font-medium mb-4">Registered Users ({users.length})</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Users who have logged in and created an account
                        </p>
                        <div className="space-y-2">
                            {users.map(u => (
                                <div
                                    key={u.id}
                                    className="flex items-center justify-between bg-white border border-gray-200 p-3"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[#F8BBD6]/20 flex items-center justify-center">
                                            <span className="text-sm font-medium text-[#F8BBD6]">
                                                {u.name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium">{u.name}</p>
                                            <p className="text-xs text-gray-400">{u.phone}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleViewUser(u.id)}
                                            className="text-xs px-2 py-1 border border-gray-200 hover:border-black transition-colors"
                                            data-testid={`view-user-${u.id}`}
                                        >
                                            View
                                        </button>
                                        {u.is_admin && (
                                            <span className="text-xs bg-black text-white px-2 py-1">
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Pages Tab - Edit About Us & House Rules */}
                    <TabsContent value="pages" className="mt-6">
                        {pagesLoading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-32 bg-gray-100" />
                                <Skeleton className="h-32 bg-gray-100" />
                            </div>
                        ) : editingPage === 'about' ? (
                            /* About Page Editor */
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading font-medium">Edit About Page</h3>
                                    <Button variant="ghost" size="sm" onClick={() => setEditingPage(null)}>
                                        <X className="w-4 h-4 mr-1" /> Cancel
                                    </Button>
                                </div>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Tagline</label>
                                        <Input
                                            value={pageFormData.tagline || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, tagline: e.target.value }))}
                                            placeholder="Women. Worth. Second Lives."
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Subtitle</label>
                                        <Input
                                            value={pageFormData.subtitle || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                                            placeholder="A curated charity auction salon"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Story Paragraphs (one per line)</label>
                                        <Textarea
                                            value={pageFormData.story_paragraphs?.join('\n\n') || ''}
                                            onChange={(e) => setPageFormData(prev => ({ 
                                                ...prev, 
                                                story_paragraphs: e.target.value.split('\n\n').filter(p => p.trim()) 
                                            }))}
                                            rows={8}
                                            placeholder="First paragraph...

Second paragraph...

Third paragraph..."
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Yeh Design Credit</label>
                                        <Input
                                            value={pageFormData.yeh_design_credit || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, yeh_design_credit: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Yeh Design URL</label>
                                        <Input
                                            value={pageFormData.yeh_design_url || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, yeh_design_url: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Instagram Handle</label>
                                        <Input
                                            value={pageFormData.instagram_handle || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, instagram_handle: e.target.value }))}
                                            placeholder="@galeriesyess"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Instagram URL</label>
                                        <Input
                                            value={pageFormData.instagram_url || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                                        />
                                    </div>
                                    
                                    <Button onClick={handleSaveAbout} disabled={submitting} className="w-full bg-black text-white">
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save About Page'}
                                    </Button>
                                </div>
                            </div>
                        ) : editingPage === 'house-rules' ? (
                            /* House Rules Editor */
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading font-medium">Edit House Rules</h3>
                                    <Button variant="ghost" size="sm" onClick={() => setEditingPage(null)}>
                                        <X className="w-4 h-4 mr-1" /> Cancel
                                    </Button>
                                </div>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Intro Text</label>
                                        <Input
                                            value={pageFormData.intro_text || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, intro_text: e.target.value }))}
                                            placeholder="Guidelines for our salon"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Rules ({pageFormData.rules?.length || 0})</label>
                                        <div className="space-y-4">
                                            {pageFormData.rules?.map((rule, index) => (
                                                <div key={index} className="border border-gray-200 p-3 rounded">
                                                    <Input
                                                        value={rule.title}
                                                        onChange={(e) => {
                                                            const newRules = [...pageFormData.rules];
                                                            newRules[index].title = e.target.value;
                                                            setPageFormData(prev => ({ ...prev, rules: newRules }));
                                                        }}
                                                        placeholder={`Rule ${index + 1} title`}
                                                        className="mb-2"
                                                    />
                                                    <Textarea
                                                        value={rule.content}
                                                        onChange={(e) => {
                                                            const newRules = [...pageFormData.rules];
                                                            newRules[index].content = e.target.value;
                                                            setPageFormData(prev => ({ ...prev, rules: newRules }));
                                                        }}
                                                        placeholder="Rule content (use • for bullet points)"
                                                        rows={4}
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="mt-2 text-red-500"
                                                        onClick={() => {
                                                            const newRules = pageFormData.rules.filter((_, i) => i !== index);
                                                            setPageFormData(prev => ({ ...prev, rules: newRules }));
                                                        }}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                                                    </Button>
                                                </div>
                                            ))}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    const newRules = [...(pageFormData.rules || []), { title: '', content: '' }];
                                                    setPageFormData(prev => ({ ...prev, rules: newRules }));
                                                }}
                                            >
                                                <Plus className="w-4 h-4 mr-1" /> Add Rule
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Footer Text</label>
                                        <Input
                                            value={pageFormData.footer_text || ''}
                                            onChange={(e) => setPageFormData(prev => ({ ...prev, footer_text: e.target.value }))}
                                        />
                                    </div>
                                    
                                    <Button onClick={handleSaveHouseRules} disabled={submitting} className="w-full bg-black text-white">
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save House Rules'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Page List */
                            <div className="space-y-4">
                                <h3 className="font-heading font-medium">Editable Pages</h3>
                                
                                {/* About Page Card */}
                                <div className="border border-gray-200 p-4 rounded">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium">About Us</h4>
                                            <p className="text-sm text-gray-400">Company info, founders, design credits</p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setPageFormData(aboutContent || {});
                                                setEditingPage('about');
                                            }}
                                        >
                                            <Pencil className="w-4 h-4 mr-1" /> Edit
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* House Rules Card */}
                                <div className="border border-gray-200 p-4 rounded">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium">House Rules</h4>
                                            <p className="text-sm text-gray-400">{houseRulesContent?.rules?.length || 0} rules</p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setPageFormData(houseRulesContent || {});
                                                setEditingPage('house-rules');
                                            }}
                                        >
                                            <Pencil className="w-4 h-4 mr-1" /> Edit
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* User Detail Dialog */}
                <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading">User Details</DialogTitle>
                        </DialogHeader>
                        {userDetailLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin" />
                            </div>
                        ) : selectedUser ? (
                            <div className="space-y-4 mt-4">
                                {/* Basic Info */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 rounded-full bg-[#F8BBD6]/20 flex items-center justify-center">
                                            <span className="text-lg font-medium text-[#F8BBD6]">
                                                {selectedUser.user.name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-heading font-medium text-lg">{selectedUser.user.name}</p>
                                            <p className="text-sm text-gray-500">{selectedUser.user.phone}</p>
                                        </div>
                                    </div>
                                    {selectedUser.user.is_admin && (
                                        <span className="text-xs bg-black text-white px-2 py-1">
                                            Admin
                                        </span>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-white border border-gray-200 p-3">
                                        <p className="text-2xl font-mono font-bold">{selectedUser.items_count}</p>
                                        <p className="text-xs text-gray-400">Auction Items</p>
                                    </div>
                                    <div className="bg-white border border-gray-200 p-3">
                                        <p className="text-2xl font-mono font-bold">{selectedUser.tickets_count}</p>
                                        <p className="text-xs text-gray-400">Tickets Purchased</p>
                                    </div>
                                </div>

                                {/* Attendance */}
                                {selectedUser.attendances?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium mb-2">Event Attendance</p>
                                        {selectedUser.attendances.map((att, idx) => (
                                            <div key={idx} className="text-sm text-gray-500 flex justify-between">
                                                <span>Swap Credits: {att.swap_credits}</span>
                                                <span>{att.checked_in_at ? 'Checked In' : 'Not Checked In'}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="pt-4 border-t border-gray-100">
                                    {!selectedUser.user.is_admin && (
                                        <Button
                                            onClick={() => handleDeleteUser(selectedUser.user.id)}
                                            variant="outline"
                                            className="w-full text-red-500 border-red-200 hover:bg-red-50"
                                            data-testid="delete-user-btn"
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Delete User
                                        </Button>
                                    )}
                                    {selectedUser.user.is_admin && (
                                        <p className="text-xs text-gray-400 text-center">
                                            Admin users cannot be deleted
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </DialogContent>
                </Dialog>

                {/* Tickets Manager Dialog */}
                <Dialog open={showTicketsManager} onOpenChange={setShowTicketsManager}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Tickets Beheren</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-4">
                            {allTickets.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">Geen tickets gevonden</p>
                            ) : (
                                allTickets.filter(t => t.payment_status === 'paid').map(ticket => (
                                    <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-sm">{ticket.user_name}</p>
                                            <p className="text-xs text-gray-500">{ticket.user_phone}</p>
                                            <p className="text-xs text-gray-400">Status: {ticket.payment_status}</p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteTicket(ticket.id)}
                                            className="text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Donations Manager Dialog */}
                <Dialog open={showDonationsManager} onOpenChange={setShowDonationsManager}>
                    <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Donaties Beheren</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 mt-4">
                            {allDonations.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">Geen donaties gevonden</p>
                            ) : (
                                allDonations.filter(d => d.payment_status === 'paid').map(donation => (
                                    <div key={donation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-sm">{donation.user_name}</p>
                                            <p className="text-xs text-gray-500">{donation.user_phone}</p>
                                            <p className="text-lg font-mono text-green-600">€{donation.amount?.toFixed(2)}</p>
                                            {donation.note && <p className="text-xs text-gray-400">{donation.note}</p>}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteDonation(donation.id)}
                                            className="text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Add Manual Donation Dialog */}
                <Dialog open={showAddDonation} onOpenChange={setShowAddDonation}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Donatie Toevoegen</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div>
                                <label className="text-sm font-medium">Naam donor</label>
                                <Input
                                    value={manualDonation.donor_name}
                                    onChange={(e) => setManualDonation(prev => ({ ...prev, donor_name: e.target.value }))}
                                    placeholder="Bijv. Jan Jansen"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Bedrag (€)</label>
                                <Input
                                    type="number"
                                    value={manualDonation.amount}
                                    onChange={(e) => setManualDonation(prev => ({ ...prev, amount: e.target.value }))}
                                    placeholder="25"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Notitie (optioneel)</label>
                                <Input
                                    value={manualDonation.note}
                                    onChange={(e) => setManualDonation(prev => ({ ...prev, note: e.target.value }))}
                                    placeholder="Bijv. Contant ontvangen"
                                />
                            </div>
                            <Button
                                onClick={async () => {
                                    if (!manualDonation.donor_name || !manualDonation.amount) {
                                        toast.error('Vul naam en bedrag in');
                                        return;
                                    }
                                    try {
                                        await api().post('/admin/finance/donations/manual', {
                                            donor_name: manualDonation.donor_name,
                                            amount: parseFloat(manualDonation.amount),
                                            note: manualDonation.note,
                                            event_id: selectedEventFinance
                                        });
                                        toast.success('Donatie toegevoegd');
                                        setShowAddDonation(false);
                                        setManualDonation({ donor_name: '', amount: '', note: '' });
                                        fetchEventFinanceData(selectedEventFinance);
                                        fetchFinanceData();
                                    } catch (err) {
                                        toast.error('Kon donatie niet toevoegen');
                                    }
                                }}
                                className="w-full bg-[#F8BBD6] hover:bg-[#f0a8c8] text-black"
                            >
                                Toevoegen
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </main>
        </div>
    );
};

export default AdminPage;
